import * as THREE from "three";
import BaseThreeScene from "../../scenes/BaseThreeScene";
import { createSeatBlock } from "../../scenes/arrayPrimitives";
import {
    createTextSprite,
    updateTextSprite,
} from "../../scenes/shared/textSpritePrimitives";

const DEFAULT_INPUT = {
    tasks: ["A", "A", "A", "B", "B", "B"],
    cooldown: 2,
};

const TASK_PALETTE = [
    0x60a5fa,
    0x22c55e,
    0xf59e0b,
    0xef4444,
    0xa855f7,
    0x14b8a6,
    0xf97316,
    0xe879f9,
];

const TASK_BASE_COLOR = new THREE.Color(0x334155);
const READY_HALO_COLOR = new THREE.Color(0x38bdf8);
const ACTIVE_COLOR = new THREE.Color(0xfbbf24);
const COOLING_COLOR = new THREE.Color(0x22d3ee);
const SLOT_PENDING_COLOR = new THREE.Color(0x1f2937);
const SLOT_IDLE_COLOR = new THREE.Color(0x7c2d12);
const SLOT_COMPLETE_COLOR = new THREE.Color(0x10b981);

const TASK_TILE_WIDTH = 1.35;
const TASK_TILE_HEIGHT = 0.48;
const TASK_TILE_DEPTH = 1.35;

const TASK_SPACING_WIDE = 2.1;
const TASK_SPACING_DENSE = 1.75;

function normalizeTaskSchedulerInput(input) {
    if (!input || typeof input !== "object" || Array.isArray(input)) {
        return {
            tasks: DEFAULT_INPUT.tasks.slice(),
            cooldown: DEFAULT_INPUT.cooldown,
        };
    }

    const tasks = Array.isArray(input.tasks)
        ? input.tasks.map((task) => String(task))
        : DEFAULT_INPUT.tasks.slice();

    const cooldown = Number.isInteger(input.cooldown)
        ? Math.max(0, input.cooldown)
        : DEFAULT_INPUT.cooldown;

    return {
        tasks,
        cooldown,
    };
}

function buildCountsByTask(tasks) {
    const countsByTask = {};

    tasks.forEach((taskId) => {
        countsByTask[taskId] = (countsByTask[taskId] || 0) + 1;
    });

    return countsByTask;
}

function getTaskOrder(countsByTask) {
    return Object.keys(countsByTask).sort((left, right) =>
        left.localeCompare(right)
    );
}

function getFormulaEstimate(countsByTask, cooldown, taskCount) {
    const counts = Object.values(countsByTask);
    const maxCount = Math.max(...counts);
    const maxCountTaskTypes = counts.filter(
        (count) => count === maxCount
    ).length;

    return Math.max(
        (maxCount - 1) * (cooldown + 1) + maxCountTaskTypes,
        taskCount
    );
}

function cloneCooldownEntries(cooldownEntries) {
    return cooldownEntries.map((entry) => ({
        taskId: entry.taskId,
        availableAt: entry.availableAt,
    }));
}

function getTimelineLayout(totalIntervals) {
    if (totalIntervals >= 28) {
        return {
            spacing: 0.8,
            slotWidth: 0.78,
            slotHeight: 0.26,
            slotDepth: 0.78,
            slotLabelWidth: 136,
            slotLabelHeight: 58,
            slotLabelFontSize: 22,
            slotLabelScale: [0.82, 0.3],
            slotLabelYOffset: 0.68,
            indexLabelWidth: 92,
            indexLabelHeight: 42,
            indexLabelFontSize: 17,
            indexLabelScale: [0.46, 0.19],
            indexLabelYOffset: -0.58,
            beamWidth: 0.72,
            beamHeight: 4.4,
        };
    }

    if (totalIntervals >= 22) {
        return {
            spacing: 0.92,
            slotWidth: 0.88,
            slotHeight: 0.28,
            slotDepth: 0.88,
            slotLabelWidth: 146,
            slotLabelHeight: 62,
            slotLabelFontSize: 24,
            slotLabelScale: [0.88, 0.33],
            slotLabelYOffset: 0.72,
            indexLabelWidth: 98,
            indexLabelHeight: 44,
            indexLabelFontSize: 18,
            indexLabelScale: [0.5, 0.2],
            indexLabelYOffset: -0.62,
            beamWidth: 0.78,
            beamHeight: 4.5,
        };
    }

    if (totalIntervals >= 16) {
        return {
            spacing: 1.08,
            slotWidth: 0.98,
            slotHeight: 0.31,
            slotDepth: 0.98,
            slotLabelWidth: 158,
            slotLabelHeight: 66,
            slotLabelFontSize: 27,
            slotLabelScale: [0.93, 0.36],
            slotLabelYOffset: 0.78,
            indexLabelWidth: 106,
            indexLabelHeight: 46,
            indexLabelFontSize: 20,
            indexLabelScale: [0.54, 0.22],
            indexLabelYOffset: -0.68,
            beamWidth: 0.86,
            beamHeight: 4.65,
        };
    }

    return {
        spacing: 1.38,
        slotWidth: 1.08,
        slotHeight: 0.34,
        slotDepth: 1.08,
        slotLabelWidth: 170,
        slotLabelHeight: 72,
        slotLabelFontSize: 30,
        slotLabelScale: [0.98, 0.38],
        slotLabelYOffset: 0.82,
        indexLabelWidth: 112,
        indexLabelHeight: 48,
        indexLabelFontSize: 22,
        indexLabelScale: [0.58, 0.24],
        indexLabelYOffset: -0.72,
        beamWidth: 0.96,
        beamHeight: 4.8,
    };
}

function buildTaskSchedulerSteps({ tasks, cooldown }) {
    const countsByTask = buildCountsByTask(tasks);
    const taskOrder = getTaskOrder(countsByTask);
    const remainingCounts = { ...countsByTask };
    const schedule = [];
    const steps = [
        {
            type: "start",
            cooldown,
            countsByTask: { ...countsByTask },
            taskOrder: taskOrder.slice(),
            totalTaskCount: tasks.length,
            formulaEstimate: getFormulaEstimate(
                countsByTask,
                cooldown,
                tasks.length
            ),
        },
    ];

    const totalTaskCount = tasks.length;
    let cooldownEntries = [];
    let completedTaskCount = 0;
    let idleCount = 0;
    let interval = 0;

    while (completedTaskCount < totalTaskCount) {
        const releasedTaskIds = cooldownEntries
            .filter((entry) => entry.availableAt <= interval)
            .map((entry) => entry.taskId);

        cooldownEntries = cooldownEntries.filter(
            (entry) => entry.availableAt > interval
        );

        const coolingTaskIdSet = new Set(
            cooldownEntries.map((entry) => entry.taskId)
        );

        const readyTaskIds = taskOrder
            .filter(
                (taskId) =>
                    remainingCounts[taskId] > 0 &&
                    !coolingTaskIdSet.has(taskId)
            )
            .sort((left, right) => {
                const countDelta = remainingCounts[right] - remainingCounts[left];

                if (countDelta !== 0) {
                    return countDelta;
                }

                return left.localeCompare(right);
            });

        if (readyTaskIds.length > 0) {
            const taskId = readyTaskIds[0];
            const remainingCountsBefore = { ...remainingCounts };
            const remainingBefore = remainingCounts[taskId];
            remainingCounts[taskId] -= 1;
            const remainingAfter = remainingCounts[taskId];
            const nextAvailableAt =
                remainingAfter > 0 && cooldown > 0
                    ? interval + cooldown + 1
                    : null;

            if (nextAvailableAt !== null) {
                cooldownEntries.push({
                    taskId,
                    availableAt: nextAvailableAt,
                });
            }

            cooldownEntries.sort((left, right) => {
                if (left.availableAt !== right.availableAt) {
                    return left.availableAt - right.availableAt;
                }

                return left.taskId.localeCompare(right.taskId);
            });

            schedule.push(taskId);
            completedTaskCount += 1;

            steps.push({
                type: "schedule-task",
                interval,
                cooldown,
                taskId,
                releasedTaskIds,
                readyTaskIds: readyTaskIds.slice(),
                remainingCountsBefore,
                remainingCounts: { ...remainingCounts },
                cooldownEntries: cloneCooldownEntries(cooldownEntries),
                schedule: schedule.slice(),
                idleCount,
                remainingTaskCount: totalTaskCount - completedTaskCount,
                remainingBefore,
                remainingAfter,
                nextAvailableAt,
                formulaEstimate: getFormulaEstimate(
                    countsByTask,
                    cooldown,
                    totalTaskCount
                ),
            });
        } else {
            schedule.push("idle");
            idleCount += 1;

            steps.push({
                type: "idle",
                interval,
                cooldown,
                releasedTaskIds,
                readyTaskIds: [],
                remainingCounts: { ...remainingCounts },
                cooldownEntries: cloneCooldownEntries(cooldownEntries),
                schedule: schedule.slice(),
                idleCount,
                remainingTaskCount: totalTaskCount - completedTaskCount,
                formulaEstimate: getFormulaEstimate(
                    countsByTask,
                    cooldown,
                    totalTaskCount
                ),
            });
        }

        interval += 1;
    }

    steps.push({
        type: "complete",
        cooldown,
        countsByTask: { ...countsByTask },
        remainingCounts: { ...remainingCounts },
        cooldownEntries: [],
        schedule: schedule.slice(),
        idleCount,
        totalIntervals: schedule.length,
        formulaEstimate: getFormulaEstimate(
            countsByTask,
            cooldown,
            totalTaskCount
        ),
    });

    return steps;
}

function formatReadyTasks(readyTaskIds, remainingCounts) {
    if (!readyTaskIds || readyTaskIds.length === 0) {
        return "-";
    }

    return readyTaskIds
        .map((taskId) => `${taskId}(${remainingCounts[taskId]})`)
        .join(", ");
}

function formatCoolingTasks(cooldownEntries) {
    if (!cooldownEntries || cooldownEntries.length === 0) {
        return "-";
    }

    return cooldownEntries
        .map((entry) => `${entry.taskId}@${entry.availableAt}`)
        .join(", ");
}

function formatRemainingTasks(remainingCounts, taskOrder) {
    const parts = taskOrder
        .filter((taskId) => remainingCounts[taskId] > 0)
        .map((taskId) => `${taskId}:${remainingCounts[taskId]}`);

    return parts.length > 0 ? parts.join("  ") : "none";
}

function createStepLogEntry(step, stepIndex, taskOrder) {
    const stepNumber = stepIndex + 1;

    switch (step.type) {
        case "start": {
            const countSummary = taskOrder
                .map((taskId) => `${taskId}:${step.countsByTask[taskId]}`)
                .join(", ");

            return {
                id: `task-scheduler-step-${stepNumber}-start`,
                stepIndex: stepNumber,
                kind: "Setup",
                title: "Initialize scheduler",
                message: `Tasks are counted by type before the interval loop begins. Remaining counts are ${countSummary}, cooldown is ${step.cooldown}, and the schedule cannot be shorter than ${step.formulaEstimate} intervals.`,
            };
        }

        case "schedule-task": {
            const releaseMessage =
                step.releasedTaskIds.length > 0
                    ? ` Released from cooldown: ${step.releasedTaskIds.join(
                          ", "
                      )}.`
                    : "";

            const cooldownMessage =
                step.nextAvailableAt === null
                    ? ` ${step.taskId} is fully exhausted after this run.`
                    : ` ${step.taskId} enters cooldown and can run again at interval ${step.nextAvailableAt}.`;

            return {
                id: `task-scheduler-step-${stepNumber}-run-${step.interval}`,
                stepIndex: stepNumber,
                kind: "Run",
                title: `Interval ${step.interval}: schedule ${step.taskId}`,
                message: `The ready pool before selection is ${formatReadyTasks(
                    step.readyTaskIds,
                    step.remainingCountsBefore
                )}. The highest-frequency ready task is ${
                    step.taskId
                }, so it is appended to the timeline.${releaseMessage}${cooldownMessage}`,
            };
        }

        case "idle": {
            const nextWakeUp = step.cooldownEntries[0]
                ? ` The earliest release is ${step.cooldownEntries[0].taskId} at interval ${step.cooldownEntries[0].availableAt}.`
                : "";

            return {
                id: `task-scheduler-step-${stepNumber}-idle-${step.interval}`,
                stepIndex: stepNumber,
                kind: "Idle",
                title: `Interval ${step.interval}: idle`,
                message: `No task is ready at this interval, so the CPU inserts an idle slot while the cooldown queue drains.${nextWakeUp}`,
            };
        }

        case "complete":
            return {
                id: `task-scheduler-step-${stepNumber}-complete`,
                stepIndex: stepNumber,
                kind: "Result",
                title: "Schedule complete",
                message: `The final timeline uses ${step.totalIntervals} intervals with ${step.idleCount} idle slots. The simulated schedule length matches the closed-form lower bound of ${step.formulaEstimate}.`,
            };

        default:
            return {
                id: `task-scheduler-step-${stepNumber}-unknown`,
                stepIndex: stepNumber,
                kind: "Step",
                title: "Advance step",
                message: "Advances the task scheduler visualization to the next interval.",
            };
    }
}

function getTaskColor(taskId) {
    const charCode = taskId.charCodeAt(0) || 65;
    return new THREE.Color(
        TASK_PALETTE[(charCode - 65) % TASK_PALETTE.length]
    );
}

function getTimelineSlotLabel(value) {
    if (!value) {
        return "";
    }

    if (value === "idle") {
        return "idle";
    }

    return value;
}

export default class TaskSchedulerScene extends BaseThreeScene {
    setup(props) {
        this.input = normalizeTaskSchedulerInput(props.input);
        this.countsByTask = buildCountsByTask(this.input.tasks);
        this.taskOrder = getTaskOrder(this.countsByTask);
        this.steps = buildTaskSchedulerSteps(this.input);
        this.finalSchedule = this.steps[this.steps.length - 1].schedule.slice();
        this.formulaEstimate = getFormulaEstimate(
            this.countsByTask,
            this.input.cooldown,
            this.input.tasks.length
        );

        const taskTypeCount = this.taskOrder.length;
        const totalIntervals = Math.max(1, this.finalSchedule.length);
        const taskSpacing =
            taskTypeCount > 6 ? TASK_SPACING_DENSE : TASK_SPACING_WIDE;

        this.timelineLayout = getTimelineLayout(totalIntervals);

        const taskRowWidth = Math.max(0, (taskTypeCount - 1) * taskSpacing);
        const timelineWidth = Math.max(
            0,
            (totalIntervals - 1) * this.timelineLayout.spacing
        );
        const dominantWidth = Math.max(taskRowWidth, timelineWidth);

        this.scene.background = new THREE.Color(0x030712);
        this.scene.fog = new THREE.Fog(0x030712, 11, 30);
        this.camera.position.set(0, 4.7, 9.4 + dominantWidth * 0.16);
        this.camera.lookAt(0, 0.6, 0);

        const ambientLight = new THREE.AmbientLight(0xffffff, 1.05);
        this.scene.add(ambientLight);

        const keyLight = new THREE.DirectionalLight(0xffffff, 1.7);
        keyLight.position.set(4, 7, 6);
        this.scene.add(keyLight);

        const leftLight = new THREE.PointLight(0x60a5fa, 22, 20, 2);
        leftLight.position.set(-4, 4, 5);
        this.scene.add(leftLight);

        const rightLight = new THREE.PointLight(0xa855f7, 18, 18, 2);
        rightLight.position.set(4, 4, 2);
        this.scene.add(rightLight);

        this.floor = new THREE.Mesh(
            new THREE.PlaneGeometry(dominantWidth + 9, 9.2),
            new THREE.MeshBasicMaterial({
                color: 0x071226,
                transparent: true,
                opacity: 0.94,
            })
        );
        this.floor.rotation.x = -Math.PI * 0.5;
        this.floor.position.set(0, -2.2, 1.1);
        this.scene.add(this.floor);

        this.currentIntervalBeam = new THREE.Mesh(
            new THREE.BoxGeometry(
                this.timelineLayout.beamWidth,
                this.timelineLayout.beamHeight,
                0.1
            ),
            new THREE.MeshBasicMaterial({
                color: 0xfbbf24,
                transparent: true,
                opacity: 0,
            })
        );
        this.currentIntervalBeam.position.set(0, 0.15, -0.95);
        this.scene.add(this.currentIntervalBeam);

        this.taskRoot = new THREE.Group();
        this.timelineRoot = new THREE.Group();
        this.scene.add(this.taskRoot);
        this.scene.add(this.timelineRoot);

        this.taskPositions = new Map();
        this.taskMeshes = new Map();
        this.taskLabels = new Map();
        this.taskCountBadges = new Map();
        this.taskCooldownBadges = new Map();
        this.taskHalos = new Map();
        this.timelinePositions = new Map();
        this.timelineSlotMeshes = new Map();
        this.timelineSlotLabels = new Map();
        this.timelineIndexLabels = new Map();

        const taskY = 1.75;
        const timelineY = -1.25;
        const taskOriginX = -taskRowWidth * 0.5;
        const timelineOriginX = -timelineWidth * 0.5;

        this.taskRowTitle = createTextSprite({
            text: "ready pool",
            width: 240,
            height: 80,
            fontSize: 28,
            textColor: "#e2e8f0",
            fillStyle: "rgba(15, 23, 42, 0.9)",
            strokeStyle: "rgba(255, 255, 255, 0.08)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 6,
            scale: [1.35, 0.45],
        });
        this.taskRowTitle.position.set(0, 4, 0);
        this.scene.add(this.taskRowTitle);

        this.timelineRowTitle = createTextSprite({
            text: "timeline",
            width: 220,
            height: 80,
            fontSize: 28,
            textColor: "#e2e8f0",
            fillStyle: "rgba(15, 23, 42, 0.9)",
            strokeStyle: "rgba(255, 255, 255, 0.08)",
            outlineColor: "rgba(0, 0, 0, 0.95)",
            outlineWidth: 6,
            scale: [1.2, 0.45],
        });
        this.timelineRowTitle.position.set(0, 0.45, 0);
        this.scene.add(this.timelineRowTitle);

        this.taskOrder.forEach((taskId, index) => {
            const x = taskOriginX + index * taskSpacing;
            const position = new THREE.Vector3(x, taskY, 0);
            const taskColor = getTaskColor(taskId);

            this.taskPositions.set(taskId, position.clone());

            const taskMesh = createSeatBlock({
                occupied: false,
                width: TASK_TILE_WIDTH,
                height: TASK_TILE_HEIGHT,
                depth: TASK_TILE_DEPTH,
                emptyColor: taskColor.getHex(),
                occupiedColor: taskColor.getHex(),
            });
            taskMesh.position.copy(position);
            taskMesh.userData.baseY = taskY;
            taskMesh.userData.taskColor = taskColor.clone();
            this.taskMeshes.set(taskId, taskMesh);
            this.taskRoot.add(taskMesh);

            const halo = new THREE.Mesh(
                new THREE.TorusGeometry(0.95, 0.06, 24, 64),
                new THREE.MeshBasicMaterial({
                    color: READY_HALO_COLOR.getHex(),
                    transparent: true,
                    opacity: 0,
                })
            );
            halo.rotation.x = Math.PI * 0.5;
            halo.position.set(x, taskY - 0.03, 0);
            this.taskHalos.set(taskId, halo);
            this.taskRoot.add(halo);

            const taskLabel = createTextSprite({
                text: taskId,
                width: 130,
                height: 78,
                fontSize: 42,
                textColor: "#ffffff",
                fillStyle: `rgba(${Math.round(taskColor.r * 255)}, ${Math.round(
                    taskColor.g * 255
                )}, ${Math.round(taskColor.b * 255)}, 0.94)`,
                strokeStyle: "rgba(255, 255, 255, 0.12)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.82, 0.48],
            });
            taskLabel.position.set(x, taskY + 0.88, 0);
            this.taskLabels.set(taskId, taskLabel);
            this.taskRoot.add(taskLabel);

            const countBadge = createTextSprite({
                text: String(this.countsByTask[taskId]),
                width: 150,
                height: 68,
                fontSize: 32,
                textColor: "#dbeafe",
                fillStyle: "rgba(15, 23, 42, 0.92)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.86, 0.38],
            });
            countBadge.position.set(x, taskY + 1.45, 0);
            this.taskCountBadges.set(taskId, countBadge);
            this.taskRoot.add(countBadge);

            const cooldownBadge = createTextSprite({
                text: "ready",
                width: 170,
                height: 62,
                fontSize: 28,
                textColor: "#ecfeff",
                fillStyle: "rgba(8, 145, 178, 0.92)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: [0.94, 0.34],
            });
            cooldownBadge.position.set(x, taskY - 0.95, 0);
            cooldownBadge.material.opacity = 0;
            this.taskCooldownBadges.set(taskId, cooldownBadge);
            this.taskRoot.add(cooldownBadge);
        });

        for (let interval = 0; interval < totalIntervals; interval += 1) {
            const x = timelineOriginX + interval * this.timelineLayout.spacing;
            const position = new THREE.Vector3(x, timelineY, 0);

            this.timelinePositions.set(interval, position.clone());

            const slotMesh = createSeatBlock({
                occupied: false,
                width: this.timelineLayout.slotWidth,
                height: this.timelineLayout.slotHeight,
                depth: this.timelineLayout.slotDepth,
                emptyColor: SLOT_PENDING_COLOR.getHex(),
                occupiedColor: SLOT_PENDING_COLOR.getHex(),
            });
            slotMesh.position.copy(position);
            slotMesh.userData.baseY = timelineY;
            this.timelineSlotMeshes.set(interval, slotMesh);
            this.timelineRoot.add(slotMesh);

            const slotLabel = createTextSprite({
                text: "",
                width: this.timelineLayout.slotLabelWidth,
                height: this.timelineLayout.slotLabelHeight,
                fontSize: this.timelineLayout.slotLabelFontSize,
                textColor: "#f8fafc",
                fillStyle: "rgba(15, 23, 42, 0.9)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
                scale: this.timelineLayout.slotLabelScale,
            });
            slotLabel.position.set(
                x,
                timelineY + this.timelineLayout.slotLabelYOffset,
                0
            );
            slotLabel.material.opacity = 0;
            this.timelineSlotLabels.set(interval, slotLabel);
            this.timelineRoot.add(slotLabel);

            const indexLabel = createTextSprite({
                text: String(interval),
                width: this.timelineLayout.indexLabelWidth,
                height: this.timelineLayout.indexLabelHeight,
                fontSize: this.timelineLayout.indexLabelFontSize,
                textColor: "#94a3b8",
                fillStyle: "rgba(2, 6, 23, 0.76)",
                strokeStyle: "rgba(255, 255, 255, 0.04)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 5,
                scale: this.timelineLayout.indexLabelScale,
            });
            indexLabel.position.set(
                x,
                timelineY + this.timelineLayout.indexLabelYOffset,
                0
            );
            this.timelineIndexLabels.set(interval, indexLabel);
            this.timelineRoot.add(indexLabel);
        }

        this.resetAnimationState();
        this.advanceStep();
    }

    resetAnimationState() {
        this.stepIndex = -1;
        this.currentStep = null;
        this.currentStepElapsed = 0;
        this.presentationTime = 0;
        this.currentInterval = null;
        this.currentTaskId = null;
        this.currentAction = "-";
        this.currentReadyTaskIds = [];
        this.currentCooldownEntries = [];
        this.currentSchedule = [];
        this.currentIdleCount = 0;
        this.remainingCounts = { ...this.countsByTask };
        this.readyCountsSnapshot = { ...this.countsByTask };
        this.totalIntervals = null;

        this.resetStepLog([]);
        this.replaceUiState({
            cooldownLabel: `${this.input.cooldown}`,
            currentIntervalLabel: "-",
            currentActionLabel: "-",
            currentTaskLabel: "-",
            readyTasksLabel: "-",
            coolingTasksLabel: "-",
            remainingTasksLabel: formatRemainingTasks(
                this.remainingCounts,
                this.taskOrder
            ),
            scheduledSlotsLabel: "0",
            idleSlotsLabel: "0",
            totalIntervalsLabel: "-",
            formulaLabel: `${this.formulaEstimate}`,
        });
        this.syncPlaybackState();
    }

    syncPlaybackState() {
        this.setPlaybackState({
            stepIndex: Math.max(0, this.stepIndex + 1),
            stepCount: this.steps.length,
            hasNextStep: this.stepIndex < this.steps.length - 1,
            isComplete:
                this.currentStep !== null && this.currentStep.type === "complete",
        });
    }

    syncUiState() {
        this.replaceUiState({
            cooldownLabel: `${this.input.cooldown}`,
            currentIntervalLabel:
                this.currentInterval === null ? "-" : `${this.currentInterval}`,
            currentActionLabel: this.currentAction,
            currentTaskLabel: this.currentTaskId || "-",
            readyTasksLabel: formatReadyTasks(
                this.currentReadyTaskIds,
                this.readyCountsSnapshot
            ),
            coolingTasksLabel: formatCoolingTasks(this.currentCooldownEntries),
            remainingTasksLabel: formatRemainingTasks(
                this.remainingCounts,
                this.taskOrder
            ),
            scheduledSlotsLabel: `${this.currentSchedule.length}`,
            idleSlotsLabel: `${this.currentIdleCount}`,
            totalIntervalsLabel:
                this.totalIntervals === null ? "-" : `${this.totalIntervals}`,
            formulaLabel: `${this.formulaEstimate}`,
        });
    }

    getStepDuration(step) {
        switch (step.type) {
            case "start":
                return 1.15;
            case "schedule-task":
                return 1.1;
            case "idle":
                return 0.95;
            case "complete":
                return 2.2;
            default:
                return 0.8;
        }
    }

    advanceStep() {
        const nextStepIndex = this.stepIndex + 1;

        if (nextStepIndex >= this.steps.length) {
            this.syncPlaybackState();
            this.syncUiState();
            return false;
        }

        this.stepIndex = nextStepIndex;
        this.currentStepElapsed = 0;
        this.currentStep = this.steps[this.stepIndex] || null;
        this.applyStep(this.currentStep);
        this.appendStepLog(
            createStepLogEntry(this.currentStep, this.stepIndex, this.taskOrder)
        );
        this.syncPlaybackState();
        this.syncUiState();

        return true;
    }

    applyStep(step) {
        if (!step) {
            return;
        }

        switch (step.type) {
            case "start": {
                this.currentInterval = null;
                this.currentTaskId = null;
                this.currentAction = "Initialize";
                this.currentReadyTaskIds = step.taskOrder
                    .slice()
                    .sort((left, right) => {
                        const countDelta =
                            step.countsByTask[right] - step.countsByTask[left];

                        if (countDelta !== 0) {
                            return countDelta;
                        }

                        return left.localeCompare(right);
                    });
                this.currentCooldownEntries = [];
                this.currentSchedule = [];
                this.currentIdleCount = 0;
                this.remainingCounts = { ...step.countsByTask };
                this.readyCountsSnapshot = { ...step.countsByTask };
                this.totalIntervals = null;
                break;
            }

            case "schedule-task": {
                this.currentInterval = step.interval;
                this.currentTaskId = step.taskId;
                this.currentAction = `Run ${step.taskId}`;
                this.currentReadyTaskIds = step.readyTaskIds.slice();
                this.currentCooldownEntries = cloneCooldownEntries(
                    step.cooldownEntries
                );
                this.currentSchedule = step.schedule.slice();
                this.currentIdleCount = step.idleCount;
                this.remainingCounts = { ...step.remainingCounts };
                this.readyCountsSnapshot = {
                    ...step.remainingCountsBefore,
                };
                this.totalIntervals = null;
                break;
            }

            case "idle": {
                this.currentInterval = step.interval;
                this.currentTaskId = null;
                this.currentAction = "Idle";
                this.currentReadyTaskIds = [];
                this.currentCooldownEntries = cloneCooldownEntries(
                    step.cooldownEntries
                );
                this.currentSchedule = step.schedule.slice();
                this.currentIdleCount = step.idleCount;
                this.remainingCounts = { ...step.remainingCounts };
                this.readyCountsSnapshot = { ...step.remainingCounts };
                this.totalIntervals = null;
                break;
            }

            case "complete": {
                this.currentInterval = null;
                this.currentTaskId = null;
                this.currentAction = "Complete";
                this.currentReadyTaskIds = [];
                this.currentCooldownEntries = [];
                this.currentSchedule = step.schedule.slice();
                this.currentIdleCount = step.idleCount;
                this.remainingCounts = { ...step.remainingCounts };
                this.readyCountsSnapshot = { ...step.remainingCounts };
                this.totalIntervals = step.totalIntervals;
                break;
            }

            default:
                break;
        }
    }

    onResetPlayback() {
        this.resetAnimationState();
        this.advanceStep();
    }

    onStepForward() {
        this.advanceStep();
    }

    update(delta) {
        if (!this.currentStep) {
            return;
        }

        const playbackDelta = this.isPlaying ? delta : 0;
        this.presentationTime += playbackDelta;
        this.currentStepElapsed += playbackDelta;

        if (
            this.isPlaying &&
            this.currentStepElapsed >= this.getStepDuration(this.currentStep)
        ) {
            const advanced = this.advanceStep();
            if (!advanced) {
                this.pause();
            }
        }

        this.updateTaskTiles(this.presentationTime);
        this.updateTimeline(this.presentationTime);
        this.updateCurrentIntervalBeam(this.presentationTime);
    }

    updateTaskTiles(elapsed) {
        const coolingByTask = new Map();
        this.currentCooldownEntries.forEach((entry) => {
            coolingByTask.set(entry.taskId, entry.availableAt);
        });

        const readyTaskIdSet = new Set(this.currentReadyTaskIds);

        this.taskOrder.forEach((taskId, index) => {
            const taskMesh = this.taskMeshes.get(taskId);
            const taskLabel = this.taskLabels.get(taskId);
            const countBadge = this.taskCountBadges.get(taskId);
            const cooldownBadge = this.taskCooldownBadges.get(taskId);
            const halo = this.taskHalos.get(taskId);
            const basePosition = this.taskPositions.get(taskId);
            const taskColor = taskMesh.userData.taskColor;
            const remainingCount = this.remainingCounts[taskId] || 0;
            const isActive = this.currentTaskId === taskId;
            const isCooling = coolingByTask.has(taskId);
            const isReady = readyTaskIdSet.has(taskId);

            let targetColor = taskColor.clone();
            let targetScale = 1;
            let targetEmissiveIntensity = 0.22;

            if (remainingCount === 0) {
                targetColor = TASK_BASE_COLOR.clone();
                targetScale = 0.92;
                targetEmissiveIntensity = 0.08;
            }

            if (isCooling) {
                targetColor = taskColor.clone().lerp(COOLING_COLOR, 0.48);
                targetScale = 0.98;
                targetEmissiveIntensity = 0.26;
            }

            if (isReady) {
                targetColor = taskColor.clone().lerp(READY_HALO_COLOR, 0.22);
                targetScale = 1.04;
                targetEmissiveIntensity = 0.36;
            }

            if (isActive) {
                targetColor = ACTIVE_COLOR.clone();
                targetScale =
                    1.13 + Math.sin(elapsed * 6 + index * 0.4) * 0.02;
                targetEmissiveIntensity = 0.95;
            }

            taskMesh.material.color.lerp(targetColor, 0.16);
            taskMesh.material.emissive.lerp(targetColor, 0.16);
            taskMesh.material.emissiveIntensity = THREE.MathUtils.lerp(
                taskMesh.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            const nextScale = THREE.MathUtils.lerp(
                taskMesh.scale.x,
                targetScale,
                0.16
            );

            taskMesh.scale.set(
                nextScale,
                THREE.MathUtils.lerp(taskMesh.scale.y, targetScale, 0.16),
                nextScale
            );

            taskMesh.position.y = THREE.MathUtils.lerp(
                taskMesh.position.y,
                basePosition.y +
                    Math.sin(elapsed * 1.9 + index * 0.35) * 0.03,
                0.12
            );

            halo.position.y = THREE.MathUtils.lerp(
                halo.position.y,
                taskMesh.position.y - 0.02,
                0.18
            );

            halo.material.opacity = THREE.MathUtils.lerp(
                halo.material.opacity,
                isReady || isActive ? 0.72 : 0,
                0.16
            );

            halo.material.color.lerp(
                isActive ? ACTIVE_COLOR : READY_HALO_COLOR,
                0.16
            );

            updateTextSprite(taskLabel, taskId, {
                fontSize: 42,
                textColor: "#ffffff",
                fillStyle: isActive
                    ? "rgba(146, 64, 14, 0.96)"
                    : `rgba(${Math.round(taskColor.r * 255)}, ${Math.round(
                          taskColor.g * 255
                      )}, ${Math.round(taskColor.b * 255)}, 0.94)`,
                strokeStyle: "rgba(255, 255, 255, 0.12)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
            });

            taskLabel.position.y = THREE.MathUtils.lerp(
                taskLabel.position.y,
                taskMesh.position.y + 0.88,
                0.18
            );

            updateTextSprite(countBadge, String(remainingCount), {
                fontSize: 32,
                textColor: remainingCount > 0 ? "#dbeafe" : "#94a3b8",
                fillStyle:
                    remainingCount > 0
                        ? "rgba(15, 23, 42, 0.92)"
                        : "rgba(15, 23, 42, 0.52)",
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
            });

            countBadge.position.y = THREE.MathUtils.lerp(
                countBadge.position.y,
                taskMesh.position.y + 1.45,
                0.18
            );

            if (isCooling) {
                updateTextSprite(cooldownBadge, `@${coolingByTask.get(taskId)}`, {
                    fontSize: 28,
                    textColor: "#ecfeff",
                    fillStyle: "rgba(8, 145, 178, 0.92)",
                    strokeStyle: "rgba(255, 255, 255, 0.08)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                });

                cooldownBadge.material.opacity = THREE.MathUtils.lerp(
                    cooldownBadge.material.opacity,
                    0.96,
                    0.16
                );
            } else if (isReady && remainingCount > 0) {
                updateTextSprite(cooldownBadge, "ready", {
                    fontSize: 28,
                    textColor: "#eff6ff",
                    fillStyle: "rgba(37, 99, 235, 0.92)",
                    strokeStyle: "rgba(255, 255, 255, 0.08)",
                    outlineColor: "rgba(0, 0, 0, 0.95)",
                    outlineWidth: 6,
                });

                cooldownBadge.material.opacity = THREE.MathUtils.lerp(
                    cooldownBadge.material.opacity,
                    0.92,
                    0.16
                );
            } else {
                cooldownBadge.material.opacity = THREE.MathUtils.lerp(
                    cooldownBadge.material.opacity,
                    0,
                    0.16
                );
            }

            cooldownBadge.position.y = THREE.MathUtils.lerp(
                cooldownBadge.position.y,
                taskMesh.position.y - 0.95,
                0.18
            );
        });
    }

    updateTimeline(elapsed) {
        const scheduledLength = this.currentSchedule.length;

        this.timelineSlotMeshes.forEach((slotMesh, interval) => {
            const slotLabel = this.timelineSlotLabels.get(interval);
            const slotValue =
                interval < scheduledLength
                    ? this.currentSchedule[interval]
                    : null;
            const isCurrent = this.currentInterval === interval;
            const isFinalComplete =
                this.currentStep && this.currentStep.type === "complete";

            let targetColor = SLOT_PENDING_COLOR.clone();
            let targetScale = 1;
            let targetEmissiveIntensity = 0.05;
            let labelOpacity = 0;
            let labelFill = "rgba(15, 23, 42, 0.9)";
            let labelTextColor = "#f8fafc";

            if (slotValue) {
                if (slotValue === "idle") {
                    targetColor = SLOT_IDLE_COLOR.clone();
                    labelFill = "rgba(124, 45, 18, 0.94)";
                    labelTextColor = "#fff7ed";
                } else {
                    targetColor = getTaskColor(slotValue);
                    labelFill = `rgba(${Math.round(
                        targetColor.r * 255
                    )}, ${Math.round(targetColor.g * 255)}, ${Math.round(
                        targetColor.b * 255
                    )}, 0.94)`;
                    labelTextColor = "#ffffff";
                }

                targetEmissiveIntensity = 0.3;
                labelOpacity = 1;
            }

            if (isCurrent) {
                targetColor = ACTIVE_COLOR.clone();
                targetScale =
                    1.12 + Math.sin(elapsed * 5.8 + interval * 0.3) * 0.02;
                targetEmissiveIntensity = 0.88;
                labelFill = "rgba(146, 64, 14, 0.96)";
                labelTextColor = "#ffffff";
            } else if (isFinalComplete && slotValue) {
                targetColor =
                    slotValue === "idle"
                        ? SLOT_IDLE_COLOR.clone()
                        : SLOT_COMPLETE_COLOR.clone().lerp(
                              getTaskColor(slotValue),
                              0.45
                          );
                targetScale = 1.04;
                targetEmissiveIntensity = 0.38;
            }

            slotMesh.material.color.lerp(targetColor, 0.16);
            slotMesh.material.emissive.lerp(targetColor, 0.16);
            slotMesh.material.emissiveIntensity = THREE.MathUtils.lerp(
                slotMesh.material.emissiveIntensity,
                targetEmissiveIntensity,
                0.16
            );

            const nextScale = THREE.MathUtils.lerp(
                slotMesh.scale.x,
                targetScale,
                0.16
            );

            slotMesh.scale.set(
                nextScale,
                THREE.MathUtils.lerp(slotMesh.scale.y, targetScale, 0.16),
                nextScale
            );

            slotMesh.position.y = THREE.MathUtils.lerp(
                slotMesh.position.y,
                slotMesh.userData.baseY +
                    (slotValue
                        ? Math.sin(elapsed * 2 + interval * 0.35) * 0.02
                        : 0),
                0.12
            );

            updateTextSprite(slotLabel, getTimelineSlotLabel(slotValue), {
                fontSize:
                    slotValue === "idle"
                        ? Math.max(18, this.timelineLayout.slotLabelFontSize - 4)
                        : this.timelineLayout.slotLabelFontSize,
                textColor: labelTextColor,
                fillStyle: labelFill,
                strokeStyle: "rgba(255, 255, 255, 0.08)",
                outlineColor: "rgba(0, 0, 0, 0.95)",
                outlineWidth: 6,
            });

            slotLabel.material.opacity = THREE.MathUtils.lerp(
                slotLabel.material.opacity,
                labelOpacity,
                0.18
            );

            slotLabel.position.y = THREE.MathUtils.lerp(
                slotLabel.position.y,
                slotMesh.position.y + this.timelineLayout.slotLabelYOffset,
                0.18
            );
        });
    }

    updateCurrentIntervalBeam(elapsed) {
        if (this.currentInterval === null) {
            this.currentIntervalBeam.material.opacity =
                THREE.MathUtils.lerp(
                    this.currentIntervalBeam.material.opacity,
                    0,
                    0.18
                );
            return;
        }

        const currentPosition = this.timelinePositions.get(this.currentInterval);

        if (!currentPosition) {
            return;
        }

        this.currentIntervalBeam.position.x = THREE.MathUtils.lerp(
            this.currentIntervalBeam.position.x,
            currentPosition.x,
            0.18
        );

        this.currentIntervalBeam.position.y = THREE.MathUtils.lerp(
            this.currentIntervalBeam.position.y,
            0.02 + Math.sin(elapsed * 3.8) * 0.05,
            0.12
        );

        this.currentIntervalBeam.material.opacity = THREE.MathUtils.lerp(
            this.currentIntervalBeam.material.opacity,
            0.32,
            0.18
        );
    }
}